import { useEffect, useRef, useState } from 'react'

import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'

import { IFRAME_LOAD_TIMEOUT_MS } from './constants'
import { isIframeEmbedBlocked, openSupportPortal } from './supportWidgetUtils'

export default function SupportIframe({ src }) {
  const iframeRef = useRef(null)
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    setStatus('loading')
    const timeoutId = window.setTimeout(() => {
      setStatus((current) => (current === 'loading' ? 'timeout' : current))
    }, IFRAME_LOAD_TIMEOUT_MS)

    const iframe = iframeRef.current
    const onNativeError = () => setStatus('blocked')
    iframe?.addEventListener('error', onNativeError)

    return () => {
      window.clearTimeout(timeoutId)
      iframe?.removeEventListener('error', onNativeError)
    }
  }, [src])

  const handleLoad = () => {
    setStatus(isIframeEmbedBlocked(iframeRef.current) ? 'blocked' : 'ready')
  }

  const handleError = () => {
    setStatus('blocked')
  }

  const showFallback = status === 'blocked' || status === 'timeout'
  const showLoading = status === 'loading'

  return (
    <div
      className="support-widget__iframe-wrap"
      aria-busy={showLoading}
      data-testid="support-widget-iframe-wrap"
    >
      {showLoading && (
        <div
          className="support-widget__status"
          data-testid="support-widget-loading"
          role="status"
          aria-live="polite"
        >
          <Spinner animation="border" size="sm" />
          <p>Loading the IT Support portal…</p>
        </div>
      )}

      {showFallback && (
        <div
          className="support-widget__status support-widget__status--fallback"
          data-testid="support-widget-fallback"
          role="alert"
        >
          <h3>Can&apos;t load the portal here</h3>
          <p>
            Your browser blocked the embedded IT Support portal. You can still
            open the full customer portal — including every request type — in a
            new tab without leaving this page.
          </p>
          <Button
            type="button"
            variant="primary"
            data-testid="support-widget-fallback-open"
            onClick={() => openSupportPortal(src)}
          >
            Open IT Support portal
          </Button>
        </div>
      )}

      <iframe
        ref={iframeRef}
        className={`support-widget__iframe${
          status === 'ready' ? '' : ' is-hidden'
        }`}
        title="Blocktickets IT Support portal"
        src={src}
        referrerPolicy="no-referrer-when-downgrade"
        allow="clipboard-write"
        onLoad={handleLoad}
        onError={handleError}
        aria-hidden={status !== 'ready'}
        data-testid="support-widget-iframe"
      />
    </div>
  )
}
